import React, { useMemo, useState } from 'react';
import { Alert, Button, Popconfirm, Space, Switch, Table, Tag, Tooltip, Typography, message } from 'antd';
import type { TableColumnsType } from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQuery } from '@apollo/client';
import { showError } from 'utils/exceptionUtils';
import {
  DELETE_LOADING_POLICY,
  GET_LOADING_POLICIES,
  GET_LOADING_POLICY_CATALOG,
  LoadingPolicyCatalog,
  LoadingPolicyItem,
  LoadingPolicySaveResult,
} from './graphql/loadingPolicyGraphql';
import { describeCheck, labelOf } from './loadingPolicyForm';
import LoadingPolicyEditDrawer from './components/LoadingPolicyEditDrawer';
import LoadingPolicyLogDrawer from './components/LoadingPolicyLogDrawer';

/**
 * 运维工具 TMS - 装车策略配置。
 *
 * 校验项目、参数与各字段可选值全部来自后端目录接口；保存前后端做全量校验（含与其他策略的匹配键重叠），
 * 不通过不落库；编辑带版本号，被他人改过会提示刷新。
 */
const LoadingPolicyPage: React.FC = () => {
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [editing, setEditing] = useState<LoadingPolicyItem | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logPolicyId, setLogPolicyId] = useState<string | null>(null);

  const { data: catalogData, loading: catalogLoading } = useQuery<{ getLoadingPolicyCatalog: LoadingPolicyCatalog }>(
    GET_LOADING_POLICY_CATALOG,
  );
  const catalog = catalogData?.getLoadingPolicyCatalog;
  const { data, loading, refetch } = useQuery<{ getLoadingPolicies: LoadingPolicyItem[] }>(GET_LOADING_POLICIES, {
    variables: { includeDeleted },
    fetchPolicy: 'network-only',
  });
  const [deletePolicy] = useMutation<{ deleteLoadingPolicy: LoadingPolicySaveResult }>(DELETE_LOADING_POLICY);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (record: LoadingPolicyItem) => {
    setEditing(record);
    setDrawerOpen(true);
  };

  const handleDelete = async (record: LoadingPolicyItem) => {
    try {
      const { data: result } = await deletePolicy({ variables: { id: record.id, version: record.version } });
      const errors = result!.deleteLoadingPolicy.validation.errors;
      if (errors.length) {
        message.error(errors.map((e) => e.message).join('；'));
      } else {
        message.success('已删除');
      }
      refetch();
    } catch (e: any) {
      showError(e);
    }
  };

  const columns: TableColumnsType<LoadingPolicyItem> = useMemo(() => {
    if (!catalog) return [];
    const labels = (values: string[] | null, options: { value: string; label: string }[]) =>
      values?.length ? values.map((v) => <Tag key={v}>{labelOf(options, v)}</Tag>) : <Typography.Text type="secondary">不限</Typography.Text>;
    return [
      { title: 'ID', dataIndex: 'id', width: 60, fixed: 'left' },
      {
        title: '名称',
        dataIndex: 'name',
        width: 200,
        fixed: 'left',
        render: (v: string, r) => (
          <Space direction="vertical" size={0}>
            <span>{v}</span>
            <Typography.Text type="secondary" className="text-xs">
              {r.businessTypes?.length ? '按业务（交接任务）' : '按站点（货物目的地）'}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '选策略条件',
        key: 'match',
        width: 280,
        render: (_, r) => (
          <div className="flex flex-col gap-1 text-xs">
            <div>扫描对象：{labelOf(catalog.loadingUnits, r.loadingUnit)}</div>
            <div>路线类型：{labels(r.routeTypes, catalog.routeTypes)}</div>
            {!!r.businessTypes?.length && <div>业务来源：{labels(r.businessTypes, catalog.businessTypes)}</div>}
            {!!r.taskTypes?.length && <div>任务类型：{labels(r.taskTypes, catalog.taskTypes)}</div>}
            <div>目的地：{labels(r.destinationWaypointTypes, catalog.destinationWaypointTypes)}</div>
            <div>解析方式：{labelOf(catalog.addressingModes, r.addressing)}</div>
          </div>
        ),
      },
      {
        title: '校验',
        key: 'checks',
        render: (_, r) => (
          <ul className="m-0 pl-4 text-xs">
            {r.checks.map((c, i) => (
              <li key={`${c.code}-${i}`}>{describeCheck(catalog, c.code, c.paramsJson, c.onFail)}</li>
            ))}
          </ul>
        ),
      },
      {
        title: '附带动作',
        key: 'sideEffects',
        width: 160,
        render: (_, r) =>
          r.sideEffects.length ? r.sideEffects.map((s) => <Tag key={s}>{labelOf(catalog.sideEffects, s).split('：')[0]}</Tag>) : '-',
      },
      {
        title: '状态',
        key: 'status',
        width: 110,
        render: (_, r) => (
          <Space direction="vertical" size={0}>
            {r.isDeleted && <Tag>已删除</Tag>}
            {r.valid ? (
              <Tag color="green">配置有效</Tag>
            ) : (
              <Tooltip title={r.parseError}>
                <Tag color="red">配置错误</Tag>
              </Tooltip>
            )}
          </Space>
        ),
      },
      {
        title: '版本 / 更新时间',
        key: 'version',
        width: 160,
        render: (_, r) => (
          <span className="text-xs">
            v{r.version}
            <br />
            {r.updatedAt ? dayjs.unix(r.updatedAt).format('YYYY-MM-DD HH:mm') : '-'}
          </span>
        ),
      },
      {
        title: '操作',
        key: 'actions',
        width: 170,
        fixed: 'right',
        render: (_, r) => (
          <Space size={0}>
            <Button type="link" size="small" disabled={r.isDeleted} onClick={() => openEdit(r)}>
              编辑
            </Button>
            <Popconfirm
              title={`确认删除策略「${r.name}」？`}
              description="删除后该类货物将选不到这条策略，可能被拦截或回退旧逻辑"
              disabled={r.isDeleted}
              onConfirm={() => handleDelete(r)}
            >
              <Button type="link" size="small" danger disabled={r.isDeleted}>
                删除
              </Button>
            </Popconfirm>
            <Button type="link" size="small" onClick={() => setLogPolicyId(r.id)}>
              变更记录
            </Button>
          </Space>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog]);

  return (
    <div className="flex flex-col gap-4 p-4">
      <Alert
        type="info"
        showIcon
        message="装车策略配置"
        description="保存前会做全量校验（参数合法、必配项齐全、不与其他策略的选策略条件重叠），不通过不保存。保存后本实例立即生效，其他实例最长 5 分钟内生效。"
      />
      <div className="flex items-center justify-between">
        <Space>
          <span>显示已删除</span>
          <Switch checked={includeDeleted} onChange={setIncludeDeleted} />
        </Space>
        <Space>
          <Button onClick={() => refetch()}>刷新</Button>
          <Button type="primary" disabled={!catalog} onClick={openCreate}>
            新增策略
          </Button>
        </Space>
      </div>
      <Table
        rowKey="id"
        size="small"
        bordered
        loading={loading || catalogLoading}
        columns={columns}
        dataSource={data?.getLoadingPolicies ?? []}
        pagination={false}
        scroll={{ x: 1500 }}
        rowClassName={(r) => (r.valid ? '' : 'bg-red-50')}
      />
      {catalog && (
        <LoadingPolicyEditDrawer
          open={drawerOpen}
          catalog={catalog}
          policy={editing}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => {
            setDrawerOpen(false);
            refetch();
          }}
        />
      )}
      <LoadingPolicyLogDrawer policyId={logPolicyId} onClose={() => setLogPolicyId(null)} />
    </div>
  );
};

export default LoadingPolicyPage;
